/*
 * Web auth store — mirrors apps/mobile/src/store/authStore.ts but uses
 * localStorage (via our storage wrapper) instead of expo-secure-store.
 *
 * Only the fields the web app needs today are here. As screens come online
 * we'll add more (biometrics, onboarding flags, gender, etc. — though most
 * of those won't apply on web).
 */

import { create } from 'zustand';
import { storage } from '../lib/storage';

const KEYS = {
  accessToken: 'auth_access_token',
  refreshToken: 'auth_refresh_token',
  userId: 'auth_user_id',
  username: 'auth_username',
  displayName: 'auth_display_name',
  // Owner-impersonation stash. When an event owner starts viewing as
  // another player we save the owner's auth here so we can restore on
  // exit. Stored under separate keys so a page refresh keeps the
  // impersonation active and a manual storage wipe still nukes both.
  impersonating: 'auth_is_impersonating',
  impersonatingTargetName: 'auth_impersonating_target_name',
  origAccessToken: 'auth_orig_access_token',
  origRefreshToken: 'auth_orig_refresh_token',
  origUserId: 'auth_orig_user_id',
  origUsername: 'auth_orig_username',
  origDisplayName: 'auth_orig_display_name',
};

interface SessionCredentials {
  userId: string | null;
  username: string | null;
  displayName: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  persistSession: boolean;
}

export interface AuthState extends SessionCredentials {
  /** Changes on identity transitions, never on a same-session token refresh. */
  sessionGeneration: number;
  isLoading: boolean;
  /** TRUE when the signed-in user is an event owner viewing the app as
   *  one of their players. Drives the impersonation banner in AppShell. */
  isImpersonating: boolean;
  /** Display name of the player being impersonated, used by the banner. */
  impersonatingTargetName: string | null;

  login: (
    userId: string,
    username: string,
    displayName: string,
    accessToken: string,
    refreshToken: string,
    stayLoggedIn: boolean,
  ) => void;
  logout: () => void;
  setAccessToken: (token: string, expectedGeneration: number) => boolean;
  /** Switch the active session to the given player. Stashes the
   *  caller's current auth so exitImpersonation can restore it. */
  startImpersonation: (target: {
    userId: string;
    username: string;
    displayName: string;
    accessToken: string;
  }) => void;
  /** Restore the original owner session. No-op if not impersonating. */
  exitImpersonation: () => void;
  /** Called once on app boot to rehydrate session from localStorage. */
  loadFromStorage: () => void;
}

/** Factory also lets request regressions exercise the real store without browser storage. */
export function createAuthStore(authStorage: typeof storage = storage) {
  let originalSession: SessionCredentials | null = null;

  function clearCredentials() {
    for (const key of Object.values(KEYS)) authStorage.removeItem(key);
  }

  function persistCredentials(session: SessionCredentials) {
    if (!session.persistSession) return;
    authStorage.setItem(KEYS.accessToken, session.accessToken ?? '');
    authStorage.setItem(KEYS.userId, session.userId ?? '');
    authStorage.setItem(KEYS.username, session.username ?? '');
    authStorage.setItem(KEYS.displayName, session.displayName ?? '');
    if (session.refreshToken) authStorage.setItem(KEYS.refreshToken, session.refreshToken);
    else authStorage.removeItem(KEYS.refreshToken);
  }

  // Another tab can replace localStorage without changing this tab's active
  // session. Never read its refresh token or overwrite its newer credentials.
  function ownsStoredSession(session: SessionCredentials) {
    return authStorage.getItem(KEYS.userId) === session.userId
      && authStorage.getItem(KEYS.refreshToken) === session.refreshToken
      && (session.refreshToken !== null
        || authStorage.getItem(KEYS.accessToken) === session.accessToken);
  }

  const emptySession: SessionCredentials = {
    userId: null, username: null, displayName: null, accessToken: null,
    refreshToken: null, persistSession: false,
  };

  return create<AuthState>((set, get) => ({
    ...emptySession,
    sessionGeneration: 0,
    isLoading: true,
    isImpersonating: false,
    impersonatingTargetName: null,

    login: (userId, username, displayName, accessToken, refreshToken, stayLoggedIn) => {
      const session = {
        userId, username, displayName: displayName ?? username,
        accessToken, refreshToken, persistSession: stayLoggedIn,
      };
      // A non-persistent login must not inherit the prior account's credentials.
      clearCredentials();
      originalSession = null;
      persistCredentials(session);
      set({
        ...session,
        sessionGeneration: get().sessionGeneration + 1,
        isLoading: false,
        isImpersonating: false,
        impersonatingTargetName: null,
      });
    },

    logout: () => {
      if (ownsStoredSession(get())) clearCredentials();
      originalSession = null;
      set({
        ...emptySession,
        sessionGeneration: get().sessionGeneration + 1,
        isLoading: false,
        isImpersonating: false,
        impersonatingTargetName: null,
      });
    },

    setAccessToken: (token, expectedGeneration) => {
      const session = get();
      if (session.sessionGeneration !== expectedGeneration) return false;
      if (session.persistSession && ownsStoredSession(session)) {
        authStorage.setItem(KEYS.accessToken, token);
      }
      set({ accessToken: token });
      return true;
    },

    startImpersonation: (target) => {
      const session = get();
      if (!session.userId || !session.accessToken || session.isImpersonating) return;
      originalSession = {
        userId: session.userId, username: session.username, displayName: session.displayName,
        accessToken: session.accessToken, refreshToken: session.refreshToken,
        persistSession: session.persistSession && ownsStoredSession(session),
      };
      if (originalSession.persistSession) {
        authStorage.setItem(KEYS.origAccessToken, originalSession.accessToken ?? '');
        authStorage.setItem(KEYS.origRefreshToken, originalSession.refreshToken ?? '');
        authStorage.setItem(KEYS.origUserId, originalSession.userId ?? '');
        authStorage.setItem(KEYS.origUsername, originalSession.username ?? '');
        authStorage.setItem(KEYS.origDisplayName, originalSession.displayName ?? '');
        authStorage.setItem(KEYS.impersonating, '1');
        authStorage.setItem(KEYS.impersonatingTargetName, target.displayName);
      }
      const targetSession = {
        ...target, refreshToken: null, persistSession: originalSession.persistSession,
      };
      // A short-lived impersonation token must never refresh as the owner.
      persistCredentials(targetSession);
      set({
        ...targetSession,
        sessionGeneration: session.sessionGeneration + 1,
        isImpersonating: true,
        impersonatingTargetName: target.displayName,
      });
    },

    exitImpersonation: () => {
      if (!get().isImpersonating) return;
      if (!originalSession?.userId || !originalSession.accessToken) {
        get().logout();
        return;
      }
      const restored = { ...originalSession };
      if (ownsStoredSession(get())) {
        clearCredentials();
        persistCredentials(restored);
      } else {
        // A different tab's login takes precedence in persisted storage.
        restored.persistSession = false;
      }
      originalSession = null;
      set({
        ...restored,
        sessionGeneration: get().sessionGeneration + 1,
        isImpersonating: false,
        impersonatingTargetName: null,
      });
    },

    loadFromStorage: () => {
      // Boot-only hydration must not replace an in-memory login on a remount.
      if (!get().isLoading) return;
      const userId = authStorage.getItem(KEYS.userId);
      const accessToken = authStorage.getItem(KEYS.accessToken);
      const isImpersonating = authStorage.getItem(KEYS.impersonating) === '1';
      originalSession = isImpersonating ? {
        userId: authStorage.getItem(KEYS.origUserId),
        username: authStorage.getItem(KEYS.origUsername),
        displayName: authStorage.getItem(KEYS.origDisplayName),
        accessToken: authStorage.getItem(KEYS.origAccessToken),
        refreshToken: authStorage.getItem(KEYS.origRefreshToken) || null,
        persistSession: true,
      } : null;
      set({
        ...(userId && accessToken ? {
          userId, accessToken,
          username: authStorage.getItem(KEYS.username),
          displayName: authStorage.getItem(KEYS.displayName),
          refreshToken: isImpersonating ? null : authStorage.getItem(KEYS.refreshToken),
          persistSession: true,
        } : emptySession),
        sessionGeneration: get().sessionGeneration + 1,
        isLoading: false,
        isImpersonating: Boolean(userId && accessToken && isImpersonating),
        impersonatingTargetName: userId && accessToken && isImpersonating
          ? authStorage.getItem(KEYS.impersonatingTargetName) : null,
      });
    },
  }));
}

export const useAuthStore = createAuthStore();
